import { ReactNode, useState, useContext, createContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { functions } from 'fb';

// Project imports
import { useUserWithLoading } from './User';
import NewsletterDialog from 'components/NewsletterDialog';
import { useSnackbar } from './Snackbar';

type NewsletterProps = {
  active: boolean;
  activateNewsletterDialog: () => void;
};

const NewsletterContext = createContext<NewsletterProps | undefined>(undefined);

const NewsletterProvider = ({ children }: { children: ReactNode }) => {
  const [active, setActive] = useState(false);
  const [display, setDisplay] = useState(false);

  const { user, isLoading: isUserLoading } = useUserWithLoading();
  const [localStorageLoaded, setLocalStorageLoaded] = useState(false);
  const [localStorageAnswer, setLocalStorageAnswer] = useState<boolean | undefined>(undefined);
  const [localStorageAnswerTimedOut, setLocalStorageAnswerTimedOut] = useState<boolean | undefined>(undefined);
  const [userNewsletterMembershipIsLoading, setUserNewsletterMembershipIsLoading] = useState(true);
  const [userNewsletterMembership, setUserNewsletterMembership] = useState<boolean | undefined>(undefined);
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation('common');

  const checkSavedLocalStorageValues = () => {
    const savedData = localStorage.getItem('newsletterAnswer');
    if (!savedData) {
      return { answer: undefined, timedOut: undefined };
    }

    try {
      const { accepted, timestamp } = JSON.parse(savedData);
      const acceptedBoolean = Boolean(accepted);

      // Check if its too old
      const savedDate = new Date(timestamp);
      const currentDate = new Date();
      const diffInDays = (currentDate.getTime() - savedDate.getTime()) / (1000 * 60 * 60 * 24);

      const timedOut = diffInDays >= 14;

      return { answer: acceptedBoolean, timedOut: timedOut };
    } catch (error) {
      return { answer: undefined, timedOut: undefined };
    }
  };

  const saveAnswerToLocalStorage = (accepted: boolean) => {
    const answerData = {
      accepted,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('newsletterAnswer', JSON.stringify(answerData));
  };

  const checkUserNewsletterMembership = async (email: string) => {
    try {
      const fetchedUserNewsletterMembership = await functions.httpsCallable('mailerLite-checkUserRegistered')({ email: email });
      if (fetchedUserNewsletterMembership) {
        // TODO: Maybe take groups into account?
        if (fetchedUserNewsletterMembership.data.registered && fetchedUserNewsletterMembership.data.active) {
          return true;
        }
      }
    } catch (error) {
      return false;
    }
    return false;
  };

  const subscribeEmailToNewsletter = (email: string) => {
    return functions
      .httpsCallable('mailerLite-registerUser')({ email: email })
      .then((response) => {
        return true;
      })
      .catch((error) => {
        return false;
      });
  };

  const activateNewsletterDialog = useCallback(() => {
    if (active) {
      setDisplay(true);
    }
  }, [active]);

  const handleClose = () => {
    handleAnswerGiven(false);
  };

  const handleConfirm = async (email: string) => {
    const success = await subscribeEmailToNewsletter(email);
    if (success) {
      handleAnswerGiven(true);
    } else {
      showSnackbar(t('COMMON.ERROR'), { severity: 'error' });
    }
  };

  const handleAnswerGiven = (accepted: boolean) => {
    setDisplay(false);
    setActive(false);
    saveAnswerToLocalStorage(accepted);
  };

  useEffect(() => {
    const { answer: savedLocalStorageAnswer, timedOut: savedLocalStorageAnswerTimedOut } = checkSavedLocalStorageValues();
    setLocalStorageAnswer(savedLocalStorageAnswer);
    setLocalStorageAnswerTimedOut(savedLocalStorageAnswerTimedOut);
    setLocalStorageLoaded(true);
  }, []);

  useEffect(() => {
    if (!isUserLoading) {
      if (user) {
        const userEmail = user?.data()?.email;
        if (userEmail) {
          checkUserNewsletterMembership(userEmail).then((checkedUserNewsletterMembership) => {
            setUserNewsletterMembership(checkedUserNewsletterMembership);
            setUserNewsletterMembershipIsLoading(false);
          });
        } else {
          setUserNewsletterMembership(false);
          setUserNewsletterMembershipIsLoading(false);
        }
      } else {
        setUserNewsletterMembership(false);
        setUserNewsletterMembershipIsLoading(false);
      }
    }
  }, [isUserLoading, user]);

  useEffect(() => {
    if (!isUserLoading && localStorageLoaded) {
      // No saved answer (undefined) or saved answer that has expired both count as
      // "we can show the popup again". A current "no" (timedOut === false) suppresses.
      const hasTimedOutOrNoAnswer = localStorageAnswerTimedOut !== false;
      if (user) {
        if (hasTimedOutOrNoAnswer && !userNewsletterMembershipIsLoading && !userNewsletterMembership) {
          setActive(true);
          return;
        }
      } else if (hasTimedOutOrNoAnswer && !localStorageAnswer) {
        setActive(true);
        return;
      }
    }
    setActive(false);
  }, [user, isUserLoading, localStorageLoaded, localStorageAnswer, localStorageAnswerTimedOut, userNewsletterMembershipIsLoading, userNewsletterMembership]);

  const contextValue = useMemo(() => ({ active, activateNewsletterDialog }), [active, activateNewsletterDialog]);

  return (
    <NewsletterContext.Provider value={contextValue}>
      {children}
      {active && <NewsletterDialog defaultEmail={user?.data()?.email} onClose={handleClose} onConfirm={handleConfirm} open={display} />}
    </NewsletterContext.Provider>
  );
};

const useNewsletter = () => {
  const context = useContext(NewsletterContext);
  if (context === undefined) {
    throw new Error('useNewsletter must be used within an NewsletterProvider');
  }
  return context;
};

const NewsletterPopupActivator = () => {
  const numSecondsDelay = 2;
  const timeoutRef = useRef<number | undefined>(undefined);
  const context = useNewsletter();

  useEffect(() => {
    if (!context.active || timeoutRef.current !== undefined) {
      return;
    }
    timeoutRef.current = window.setTimeout(() => {
      context.activateNewsletterDialog();
    }, 1000 * numSecondsDelay);
    return () => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    };
  }, [context.active, context.activateNewsletterDialog, numSecondsDelay]);

  return <></>;
};

export { NewsletterProvider, useNewsletter, NewsletterPopupActivator };
